import {
  PRODUCT_FLAG_ROWS,
  type ProductFlagFormValues,
  type ProductFlagRow,
  type SettableProductFlagField,
} from '@/definitions/productFlags'

import { hasUsableAccountSid } from './canEnableTwilio'

const ROW_BY_FIELD = new Map<SettableProductFlagField, ProductFlagRow>(
  PRODUCT_FLAG_ROWS.map((row) => [row.field, row])
)

const ALL_SETTABLE_FIELDS: readonly SettableProductFlagField[] = PRODUCT_FLAG_ROWS.map(
  (row) => row.field
)

const DEPENDENTS_BY_FIELD = PRODUCT_FLAG_ROWS.reduce<
  Map<SettableProductFlagField, SettableProductFlagField[]>
>((dependents, row) => {
  if (row.requires !== undefined) {
    dependents.set(row.requires, [...(dependents.get(row.requires) ?? []), row.field])
  }
  return dependents
}, new Map())

/**
 * The product flag another one sits behind, or null when it stands alone.
 *
 * Returns the whole row rather than the field so callers can name the requirement in
 * the same words its own switch is labelled with.
 */
export const requirementOf = (field: SettableProductFlagField): ProductFlagRow | null => {
  const requires = ROW_BY_FIELD.get(field)?.requires
  return requires === undefined ? null : ROW_BY_FIELD.get(requires) ?? null
}

/** The product flags that sit directly behind this one. */
export const dependentsOf = (
  field: SettableProductFlagField
): readonly SettableProductFlagField[] => DEPENDENTS_BY_FIELD.get(field) ?? []

/**
 * Every product flag that stops working when this one goes off, however far down.
 *
 * Switching Texting off has to reach the Quinn call product flags two steps below it,
 * not only VoiP directly beneath — so a caller clearing dependents clears these.
 */
export const descendantsOf = (
  field: SettableProductFlagField
): readonly SettableProductFlagField[] => {
  const found: SettableProductFlagField[] = []
  const pending = [...dependentsOf(field)]

  while (pending.length > 0) {
    const next = pending.shift()
    if (next === undefined || found.includes(next)) {
      continue
    }
    found.push(next)
    pending.push(...dependentsOf(next))
  }

  return found
}

/**
 * The product flags currently on that switching this one off would take with it, in
 * display order and named as their own switches name them.
 *
 * Only the ones that are on: a dialog promising to turn off a product flag the practice
 * never had describes a change that is not happening.
 */
export const enabledDependentLabelsOf = (
  values: ProductFlagFormValues,
  field: SettableProductFlagField
): string[] => {
  const affected = new Set<SettableProductFlagField>(descendantsOf(field))

  return PRODUCT_FLAG_ROWS.filter((row) => affected.has(row.field) && values[row.field]).map(
    (row) => row.label
  )
}

/**
 * Whether what this product flag sits behind is in place, so it can be switched on at
 * all.
 *
 * Only the product flag directly behind it is read. The rest of the chain is covered
 * because a requirement that is itself unavailable cannot have been switched on here —
 * and an organization already stored in a state this screen now refuses is shown as it
 * is rather than corrected out from under the operator.
 *
 * Texting is the one product flag whose requirement is a value rather than another
 * product flag, so it is answered here too: the rows behind it read `twilio_enabled`,
 * and this is what keeps them agreeing with it about what is available.
 */
export const isRequirementMet = (
  values: ProductFlagFormValues,
  field: SettableProductFlagField,
  storedAccountSid: string | null
): boolean => {
  if (field === 'twilio_enabled') {
    return hasUsableAccountSid(values.twilio_account_sid, storedAccountSid)
  }
  const requirement = requirementOf(field)
  return requirement === null || values[requirement.field]
}

/**
 * Switches off anything whose requirement is not met, following each chain up.
 *
 * For staged values only — an operator action that sets many switches at once cannot
 * check them against each other one at a time. Only the product flag graph is enforced
 * here; a requirement that is a value rather than a product flag belongs to the caller
 * staging it, which is the only place that knows what the organization already has.
 *
 * `clampable` bounds what may be switched off, for a caller acting on part of the
 * catalogue: a package choice stages only the product flags its packages own, so
 * correcting a manual switch it never touched would be an edit the operator did not ask
 * for.
 *
 * Every product flag is resolved from the values passed in, walking its own chain,
 * rather than from the corrections made so far — which is what keeps the result
 * independent of the order the catalogue is listed in, and clamping a clamped set a
 * no-op.
 *
 * Nothing is written; the result is a new set of form values.
 */
export const clampToRequirements = (
  values: ProductFlagFormValues,
  clampable: readonly SettableProductFlagField[] = ALL_SETTABLE_FIELDS
): ProductFlagFormValues => {
  const clamped: ProductFlagFormValues = { ...values }

  const resolve = (field: SettableProductFlagField): boolean => {
    if (!values[field]) {
      return false
    }
    const requirement = requirementOf(field)
    return requirement === null || resolve(requirement.field)
  }

  for (const field of clampable) {
    clamped[field] = resolve(field)
  }

  return clamped
}
